import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal, AlertTriangle } from 'lucide-react';
import { usePayrollRuns, useRunEligibility } from '@/composables/payroll/usePayrollRuns';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatCurrency, formatDateUTC } from '@/lib/formatters';
import {
  MONTH_OPTIONS,
  PAYROLL_RUN_STATUS_OPTIONS,
  PAYROLL_RUN_STATUS_STYLES,
  type PayrollRun,
} from '@/types/payroll';

const YEAR_OPTIONS = (() => {
  const now = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => String(now + 1 - i));
})();

export function PayrollRunListPage() {
  const navigate = useNavigate();
  const { runs, meta, isLoading, filters, create, cancel, remove, exportCsv } = usePayrollRuns();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [creating, setCreating] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<PayrollRun | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PayrollRun | null>(null);

  const columns: ColumnDef<PayrollRun, unknown>[] = useMemo(() => [
    {
      accessorKey: 'runNumber',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Run #" />,
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.runNumber}</span>,
    },
    {
      id: 'period',
      header: 'Period',
      cell: ({ row }) => (
        <span className="font-medium">
          {MONTH_OPTIONS[row.original.periodMonth - 1]?.label} {row.original.periodYear}
        </span>
      ),
    },
    { accessorKey: 'employeeCount', header: 'Employees', cell: ({ row }) => <span className="tabular-nums">{row.original.employeeCount}</span> },
    {
      accessorKey: 'totalGross',
      header: 'Gross',
      cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.totalGross, row.original.currency)}</span>,
    },
    {
      accessorKey: 'totalNetPay',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Net payable" />,
      cell: ({ row }) => <span className="font-medium tabular-nums">{formatCurrency(row.original.totalNetPay, row.original.currency)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant="outline" className={`border-0 font-medium ${PAYROLL_RUN_STATUS_STYLES[row.original.status] ?? ''}`}>
          {row.original.status === 'REVIEW' ? 'IN REVIEW' : row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'payDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Pay date" />,
      cell: ({ row }) => formatDateUTC(row.original.payDate),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const r = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/people/payroll-runs/${r.id}`); }}>Open</DropdownMenuItem>
              {(r.status === 'DRAFT' || r.status === 'REVIEW') && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setCancelTarget(r); }}>Cancel run</DropdownMenuItem>
              )}
              {(r.status === 'DRAFT' || r.status === 'CANCELLED') && (
                <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>Delete</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer
      title="Payroll Runs"
      description="Process a month's payslips as one batch, then post the cost to the ledger."
      actions={
        <>
          <Button variant="outline" disabled={isExporting} onClick={() => exportFile(() => exportCsv(), 'payroll-runs.csv')}>
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />New Payroll Run
          </Button>
        </>
      }
    >
      <DataTableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search run #…"
        filters={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.current.status || 'all'} onValueChange={(v) => filters.set({ status: v === 'all' ? '' : v, page: 1 })}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {PAYROLL_RUN_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.current.periodYear || 'all'} onValueChange={(v) => filters.set({ periodYear: v === 'all' ? '' : v, page: 1 })}>
              <SelectTrigger className="w-[120px]"><SelectValue placeholder="Year" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={runs}
        isLoading={isLoading}
        page={meta?.page ?? 1}
        totalPages={meta?.totalPages ?? 1}
        total={meta?.total ?? 0}
        pageSize={filters.current.limit}
        onPageChange={(p) => filters.set({ page: p })}
        onPageSizeChange={(s) => filters.set({ limit: s })}
        onRowClick={(row) => navigate(`/people/payroll-runs/${row.id}`)}
        emptyMessage="No payroll runs yet."
      />

      <CreateRunDialog
        open={creating}
        onOpenChange={setCreating}
        isPending={create.isPending}
        onCreate={(body) => create.mutate(body, { onSuccess: (res) => { setCreating(false); navigate(`/people/payroll-runs/${res.data.id}`); } })}
      />

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={() => setCancelTarget(null)}
        title="Cancel Payroll Run"
        description={`${cancelTarget?.runNumber} will be cancelled and its draft payslips removed, freeing the month to be run again. Nothing has been posted to the ledger yet.`}
        variant="destructive"
        confirmLabel="Cancel run"
        onConfirm={() => { if (cancelTarget) { cancel.mutate(cancelTarget.id); setCancelTarget(null); } }}
        isLoading={cancel.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Payroll Run"
        description={`${deleteTarget?.runNumber} and its draft payslips will be removed. This cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { remove.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={remove.isPending}
      />
    </PageContainer>
  );
}

/** Period picker plus a live preview of exactly who will be included. */
function CreateRunDialog({
  open, onOpenChange, onCreate, isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreate: (body: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  const today = new Date();
  // Payroll is run after the period closes, so default to last month.
  const defaultMonth = today.getUTCMonth() === 0 ? 12 : today.getUTCMonth();
  const defaultYear = today.getUTCMonth() === 0 ? today.getUTCFullYear() - 1 : today.getUTCFullYear();

  const [month, setMonth] = useState(String(defaultMonth));
  const [year, setYear] = useState(String(defaultYear));
  const [payDate, setPayDate] = useState('');
  const [notes, setNotes] = useState('');

  const { eligibility, isLoading } = useRunEligibility(Number(year), Number(month), open);

  const canCreate = !!eligibility && eligibility.eligible.length > 0 && !eligibility.mixedCurrency;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Payroll Run</DialogTitle>
          <DialogDescription>
            A draft payslip is generated for every eligible employee. Nothing is published or posted
            to the ledger until you approve the run.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="runPayDate">Pay date</Label>
              <Input id="runPayDate" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
              <p className="text-xs text-muted-foreground">Defaults to the last day of the period.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="runNotes">Notes</Label>
              <Input id="runNotes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          {isLoading && <p className="text-sm text-muted-foreground">Checking who is eligible…</p>}

          {eligibility?.mixedCurrency && (
            <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p className="text-xs text-destructive">
                Eligible employees are paid in more than one currency ({eligibility.currencies.join(', ')}).
                A run posts a single ledger entry, so every payslip in it must share a currency.
              </p>
            </div>
          )}

          {eligibility && (
            <div className="space-y-3">
              <div className="rounded-md border">
                <div className="border-b bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider">
                  Will be included ({eligibility.eligible.length})
                </div>
                <div className="max-h-48 divide-y overflow-y-auto">
                  {eligibility.eligible.length === 0 && (
                    <p className="px-3 py-3 text-sm text-muted-foreground">Nobody is eligible for this period.</p>
                  )}
                  {eligibility.eligible.map((e) => (
                    <div key={e.employeeId} className="flex items-center justify-between px-3 py-1.5 text-sm">
                      <span>{e.name} <span className="text-xs text-muted-foreground">{e.employeeCode}</span></span>
                      <span className="tabular-nums text-muted-foreground">
                        {e.ctcAnnual ? formatCurrency(e.ctcAnnual, e.currency ?? 'INR') : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {eligibility.skipped.length > 0 && (
                <div className="rounded-md border">
                  <div className="border-b bg-muted/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider">
                    Skipped ({eligibility.skipped.length})
                  </div>
                  <div className="max-h-40 divide-y overflow-y-auto">
                    {eligibility.skipped.map((s) => (
                      <div key={s.employeeCode} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
                        <span className="min-w-0 truncate">{s.name}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{s.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!canCreate || isPending}
            onClick={() => onCreate({
              periodMonth: Number(month),
              periodYear: Number(year),
              ...(payDate ? { payDate } : {}),
              ...(notes ? { notes } : {}),
            })}
          >
            {isPending ? 'Generating…' : `Generate ${eligibility?.eligible.length ?? 0} payslip(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
